import * as React from 'react'
import { Select, Input, InputNumber, Checkbox } from 'antd'
import {
  InventoryLogType,
  IInventoryLogFilter,
} from '@getgreenline/inventory/build/models/inventoryLog'
import { CurrentCompanyStore } from '../../stores/CurrentCompanyStore'
import { AsyncProductInput } from '../../components/GrInput/AsyncProductInput'
import { RemovableOptionSelect } from '../RemovableOptionSelect'
import { ExportInventoryLogsButton } from './ExportInventoryLogsButton'
import { inject, observer } from 'mobx-react'
import './InventoryLogsSearchFilters.scss'
import { BlazeButton } from '../CustomButtons/BlazeButton'
import { DateFilter } from '../../containers/Dashboard/DashboardReports/Financial/ReportFilter/DateFilter'
import { getCategoryName } from '../../utilities/helpers'
import { Track } from '../../Track'
import { CurrentUserStore } from '../../stores/CurrentUserStore'

export type AllFilterTypes =
  | 'location'
  | 'employee'
  | 'product'
  | 'logType'
  | 'perPage'
  | 'category'
  | 'dates'
  | 'notes'
  | 'export'
  | 'modified'

export interface InventoryProduct {
  id: string
  name: string
}

interface Props {
  currentCompanyStore: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
  inventoryLogFilter: IInventoryLogFilter
  onLogsPerPageChange: (perPage: number) => void
  hideFilters?: AllFilterTypes[]
  isLoading?: boolean
}

interface State {
  products: InventoryProduct[]
  perPageValue: number
  showFilters: AllFilterTypes[]
  filters: IInventoryLogFilter
}

interface ILogTypeOption {
  value: InventoryLogType
  label: string
}

interface IFilterTypeOption {
  value: AllFilterTypes
  label: string
}

const logTypeOptions: ILogTypeOption[] = [
  { value: InventoryLogType.SALE, label: 'Sale' },
  { value: InventoryLogType.PURCHASE_ORDER, label: 'Purchase order' },
  { value: InventoryLogType.AUDIT, label: 'Audit' },
  { value: InventoryLogType.REFUND, label: 'Refund' },
  { value: InventoryLogType.RETURN, label: 'Returned' },
  { value: InventoryLogType.DESTROYED, label: 'Destroyed' },
  { value: InventoryLogType.LOST, label: 'Lost' },
  { value: InventoryLogType.CONVERT, label: 'Conversion' },
  { value: InventoryLogType.TRANSFER, label: 'Transfer' },
]

const filterOptions: IFilterTypeOption[] = [
  { value: 'category', label: 'Category' },
  { value: 'employee', label: 'Employee' },
  { value: 'notes', label: 'Notes' },
]

@inject('currentCompanyStore')
@observer
export class InventoryLogsSearchFilters extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      products: [],
      perPageValue: props.inventoryLogFilter.limit || 20,
      showFilters: [],
      filters: props.inventoryLogFilter,
    }
  }

  componentDidMount = async () => this.props.currentCompanyStore!.productStore!.getCategories()

  onFilterChange = (filterValue: AllFilterTypes) => {
    const { showFilters } = this.state
    const selectedFilterIndex = showFilters.indexOf(filterValue)

    selectedFilterIndex !== -1
      ? showFilters.splice(selectedFilterIndex, 1)
      : showFilters.push(filterValue)

    this.setState({ showFilters: showFilters })
    Track.track('dashboard:inventory_inventoryLogFilterChanged')
  }

  onCategoryChange = (categoryIds: string[]) => {
    this.props.inventoryLogFilter.categoryIds = categoryIds
    this.setState((prevState) => ({
      filters: {
        ...prevState.filters,
        categoryIds,
      },
    }))
  }

  onLocationChange = (locationId: number | undefined) => {
    this.props.inventoryLogFilter.locationId = locationId
    this.setState((prevState) => ({
      filters: {
        ...prevState.filters,
        locationId,
      },
    }))
  }

  onEmployeeToggle = (employeeIds: string[]) => {
    this.props.inventoryLogFilter.employeeIds = employeeIds
    this.setState((prevState) => ({
      filters: {
        ...prevState.filters,
        employeeIds,
      },
    }))
  }

  onLogTypeChange = (logType: InventoryLogType | undefined) => {
    this.props.inventoryLogFilter.type = logType
    this.setState((prevState) => ({
      filters: {
        ...prevState.filters,
        type: logType,
      },
    }))
  }

  onStartDateChange = (startDate: Date | undefined) => {
    this.props.inventoryLogFilter.startDate = startDate
    this.setState((prevState) => ({
      filters: {
        ...prevState.filters,
        startDate,
      },
    }))
  }

  onEndDateChange = (endDate: Date | undefined) => {
    this.props.inventoryLogFilter.endDate = endDate
    this.setState((prevState) => ({
      filters: {
        ...prevState.filters,
        endDate,
      },
    }))
  }

  onNotesQueryChange = (notesQuery: string) => {
    this.props.inventoryLogFilter.notesQuery = notesQuery
    this.setState((prevState) => ({
      filters: {
        ...prevState.filters,
        notesQuery,
      },
    }))
  }

  onProductChange = (product: InventoryProduct) => {
    let [...newProducts] = this.state.products
    const productIds = newProducts.map((p) => p.id)
    if (productIds.includes(product.id)) {
      newProducts = newProducts.filter((p) => p.id !== product.id)
    } else {
      newProducts = [...newProducts, product]
    }
    this.props.inventoryLogFilter.productIds = newProducts.map((newProduct) => newProduct.id)
    this.setState((prevState) => ({
      filters: {
        ...prevState.filters,
        productIds: newProducts.map((newProduct) => newProduct.id),
      },
    }))
    this.setState({ products: newProducts })
  }

  onModifiedChange = (modified?: boolean) => {
    this.props.inventoryLogFilter.modified = modified
    this.setState((prevState) => ({
      filters: {
        ...prevState.filters,
        modified,
      },
    }))
  }

  render() {
    const { onLogsPerPageChange, hideFilters, isLoading } = this.props

    const {
      locationId,
      productIds,
      employeeIds,
      type,
      categoryIds,
      startDate,
      endDate,
      notesQuery,
      modified,
    } = this.state.filters

    const { showFilters, products } = this.state
    const locations = this.props.currentCompanyStore.locations || []
    const employees = this.props.currentCompanyStore.users || []
    const categories = this.props.currentCompanyStore!.productStore!.flattenedCategories
    const availableFilters: IFilterTypeOption[] = filterOptions.filter(
      (filterOption) =>
        !showFilters.includes(filterOption.value) &&
        !(hideFilters || []).includes(filterOption.value),
    )

    return (
      <div className='mb-2'>
        <div className='d-flex' style={{ flexWrap: 'wrap' }}>
          {!(hideFilters || []).includes('location') && (
            <div className='mr-2 mb-2' style={{ width: 200 }}>
              <div>
                <Select
                  className='w-100'
                  value={locationId}
                  allowClear
                  placeholder='Location'
                  onChange={(value: any) => {
                    this.onLocationChange(value as number | undefined)
                  }}
                  style={{ width: 'fit-content' }}
                  dropdownMatchSelectWidth={false}
                >
                  {locations.map((location, index) => {
                    return (
                      <Select.Option key={index} value={location.id}>
                        {location.name}
                      </Select.Option>
                    )
                  })}
                </Select>
              </div>
            </div>
          )}

          <div className='mr-2 mb-2 d-flex flex-column'>
            <div className='d-flex'>
              <DateFilter
                startDate={startDate ? startDate : null}
                endDate={endDate ? endDate : null}
                onStartDateChange={(date) => date && this.onStartDateChange(date)}
                onEndDateChange={(date) => date && this.onEndDateChange(date)}
                allowClear={false}
                maxRangeMonths={12}
              />
            </div>
          </div>

          {!(hideFilters || []).includes('product') && (
            <div className='mr-2 mb-2' style={{ width: 200 }}>
              <AsyncProductInput
                showSearch
                mode='default'
                placeholder='Search a product'
                className='w-100'
                onChange={(selectedProducts) => {
                  if (selectedProducts.length > 0) {
                    const selectedProduct = selectedProducts[0]

                    this.onProductChange({
                      id: selectedProduct.id,
                      name: selectedProduct.name,
                    })
                  }
                }}
              />
            </div>
          )}

          <div className='mr-2 mb-2' style={{ width: 200 }}>
            <Select
              allowClear
              className='w-100'
              value={type}
              placeholder='Type'
              onChange={(value: any) => {
                if (value) {
                  this.onLogTypeChange(value as InventoryLogType)
                } else {
                  this.onLogTypeChange(undefined)
                }
              }}
            >
              {logTypeOptions.map((logType, index) => {
                return (
                  <Select.Option key={index} value={logType.value}>
                    {logType.label}
                  </Select.Option>
                )
              })}
            </Select>
          </div>

          {!(hideFilters || []).includes('category') && showFilters.includes('category') && (
            <div className='mr-2 mb-2 d-flex flex-column'>
              <div className='d-flex'>
                <Select
                  style={{ width: 190 }}
                  placeholder='Category'
                  value={categoryIds}
                  mode='multiple'
                  allowClear
                  onChange={(categoryIds: any) => {
                    this.onCategoryChange(categoryIds as string[])
                  }}
                >
                  {categories.map((category, index) => {
                    return (
                      <Select.Option key={index} value={category.id}>
                        {getCategoryName(category)}
                      </Select.Option>
                    )
                  })}
                </Select>
                <span
                  onClick={() => {
                    this.onFilterChange('category')
                    this.onCategoryChange([])
                  }}
                  style={{ marginTop: 10 }}
                  className='fa fa-remove pointer text-muted ml-2'
                />
              </div>
            </div>
          )}

          {!(hideFilters || []).includes('employee') && showFilters.includes('employee') && (
            <div className='mr-2 mb-2 d-flex flex-column' style={{ width: 200 }}>
              <div className='d-flex'>
                <Select
                  className='w-100'
                  allowClear
                  placeholder='Employee'
                  value={employeeIds}
                  onChange={(value: any) => this.onEmployeeToggle(value as string[])}
                >
                  {employees.map((employee, index) => {
                    return (
                      <Select.Option key={index} value={employee.id}>
                        {employee.name}
                      </Select.Option>
                    )
                  })}
                </Select>
                <span
                  onClick={() => {
                    this.onFilterChange('employee')
                    this.onEmployeeToggle([])
                  }}
                  style={{ marginTop: 10 }}
                  className='fa fa-remove pointer text-muted ml-2'
                ></span>
              </div>
            </div>
          )}

          {!(hideFilters || []).includes('notes') && showFilters.includes('notes') && (
            <div className='mr-2 mb-2 d-flex flex-column'>
              <div className='d-flex'>
                <Input
                  value={notesQuery}
                  placeholder={'Notes'}
                  onChange={(event) => {
                    this.onNotesQueryChange(event.target.value)
                  }}
                />
                <span
                  onClick={() => {
                    this.onFilterChange('notes')
                    this.onNotesQueryChange('')
                  }}
                  style={{ marginTop: 10 }}
                  className='fa fa-remove pointer text-muted ml-2'
                />
              </div>
            </div>
          )}

          <div className='mr-2 mb-2' style={{ width: 200 }}>
            <Select
              className='w-100'
              allowClear
              placeholder='Add filter'
              value={undefined}
              onChange={(value: any) => {
                this.onFilterChange(value as AllFilterTypes)
              }}
            >
              {availableFilters.map((availableFilter, index) => {
                return (
                  <Select.Option key={index} value={availableFilter.value}>
                    {availableFilter.label}
                  </Select.Option>
                )
              })}
            </Select>
          </div>

          {this.props.currentUserStore?.currentUser?.isAdmin && (
            <div className='mb-2' style={{ display: 'flex', flexWrap: 'wrap' }}>
              <div
                className='mr-2 d-flex align-items-center'
                style={{
                  paddingLeft: '10px',
                  paddingRight: '5px',
                  textAlign: 'center',
                  height: `31px`,
                  borderStyle: `solid`,
                  borderWidth: `1px`,
                  borderColor: `#d9d9d9`,
                  background: `white`,
                }}
              >
                <Checkbox
                  checked={modified}
                  onChange={() => {
                    this.onModifiedChange(!modified)
                  }}
                >
                  Modified entries
                </Checkbox>
              </div>
            </div>
          )}

          {!(hideFilters || []).includes('perPage') && (
            <div className='mr-2 mb-2 d-flex flex-column'>
              <div className='d-flex'>
                <InputNumber
                  className='mr-2'
                  min={1}
                  max={150}
                  value={this.state.perPageValue}
                  onChange={(perPage) => this.setState({ perPageValue: perPage || 30 })}
                />
                <BlazeButton
                  buttonProps={{
                    loading: isLoading,
                    disabled: isLoading,
                    type: 'primary',
                    onClick: () => {
                      onLogsPerPageChange(this.state.perPageValue)
                    },
                  }}
                  titleProps={{
                    title: 'Apply',
                  }}
                />
              </div>
            </div>
          )}

          {!(hideFilters || []).includes('export') && (
            <div className='mr-2 mb-2 d-flex flex-row'>
              <div className='d-flex'>
                <BlazeButton
                  buttonProps={{
                    loading: isLoading,
                    disabled: isLoading,
                    type: 'primary',
                    className: 'mr-2',
                    onClick: () => {
                      onLogsPerPageChange(this.state.perPageValue)
                    },
                  }}
                  titleProps={{
                    title: isLoading ? 'Applying...' : 'Apply',
                  }}
                />
              </div>
              <ExportInventoryLogsButton
                locationId={locationId}
                employeeIds={employeeIds || []}
                logType={type}
                productIds={productIds || []}
              />
            </div>
          )}
        </div>

        {!(hideFilters || []).includes('product') && products.length > 0 && (
          <div>
            <div>Selected products:</div>
            {products.map((product) => (
              <RemovableOptionSelect
                key={product.id}
                label={product.name}
                onRemove={() => this.onProductChange(product)}
              />
            ))}
          </div>
        )}
      </div>
    )
  }
}
